// ---------------------------------------------------------------------------
// Temporadas de tema — quando um tema sazonal existe na Loja, e quando ele
// some sozinho.
//
// Blueprint: `docs/systems/seasonal-themes.md`.
//
// ## Por que a janela é um módulo, e não um `if` na vitrine
//
// Campanha com prazo e sem quem a desative é o BUG-39 (carrossel vencido
// servido em silêncio). Três superfícies precisam da MESMA resposta: a vitrine
// da Loja, o guard de débito no servidor e qualquer aviso de "últimos dias".
// Enquanto a data for comparada à mão em cada uma, uma delas some no dia certo
// e as outras não.
//
// ## 🔴 O que este módulo NÃO governa
//
// **O direito.** Quem comprou fica com o tema para sempre, e isso não passa
// por aqui: o direito mora em `unlockedThemes` (client-locked, permanente) e é
// avaliado por `resolveEquippedThemeId`, que NUNCA deve importar este arquivo.
//
// Se a janela encostar no resolvedor, no dia do fechamento todo mundo que
// comprou perde o tema em silêncio, no app do dono e no perfil público ao
// mesmo tempo. É o BUG-80 de novo, agora com item pago. Há teste dedicado
// travando isso.
//
// Estar à venda e ter o direito são coisas diferentes, e é a arquitetura de
// hoje que já as separa de graça.
//
// ## A comparação é de CHAVE DE DIA, não de `Date`
//
// `dayKeyForBR` devolve "YYYY-MM-DD" no fuso do produto, e string nesse
// formato ordena lexicograficamente igual à data. Comparar `Date` traria o
// fuso do APARELHO para dentro da decisão: quem estiver com o relógio em UTC
// veria a temporada fechar 3 horas antes. É a mesma razão pela qual a economia
// inteira do app usa `dayKeyForBR`.
// ---------------------------------------------------------------------------

import { dayKeyForBR } from "../lib/time";

export interface ThemeSeason {
  /** Estável entre edições: a de 2027 reusa este id. */
  id: string;
  /** Nome exibido. */
  label: string;
  /** Primeiro dia, chave BR, **inclusivo**. */
  firstDay: string;
  /**
   * Último dia, chave BR, **inclusivo**.
   *
   * ⚠️ Inclusivo é o que faz a temporada viver o último dia inteiro. A
   * desativação acontece na virada para o dia seguinte, 00:00 BRT.
   */
  lastDay: string;
  /** Qual edição desta temporada. O tema volta em edições futuras. */
  edition: string;
}

/**
 * As temporadas conhecidas.
 *
 * ⚠️ Só datas moram aqui. Quais temas pertencem à temporada é declarado no
 * `themeRegistry` (campo `season`), numa direção só: se a lista existisse
 * também aqui seriam duas fontes da mesma verdade, que é exatamente como
 * `themeId` e `themePreference` divergiram (BUG-80). Quem precisar de "os
 * temas da temporada X" deriva de `THEMES`.
 */
export const THEME_SEASONS: readonly ThemeSeason[] = [
  {
    id: "eclipse",
    label: "Eclipse",
    // Abre depois da Bienal (que termina 13/09) e no começo limpo de um mês.
    // Estas duas datas são o único lugar onde a janela existe: mover a
    // temporada é editar estas linhas, e mais nada.
    firstDay: "2026-10-01",
    lastDay: "2026-11-30",
    edition: "2026",
  },
];

/** A temporada de um id, ou `null` se o id não existe. */
export function getThemeSeason(seasonId: string | null | undefined): ThemeSeason | null {
  if (!seasonId) return null;
  return THEME_SEASONS.find((s) => s.id === seasonId) ?? null;
}

/**
 * A temporada está no ar?
 *
 * ⚠️ Temporada DESCONHECIDA devolve `false`, e a escolha é deliberada: falha
 * fechada. Um `season: "eclispe"` com typo deixaria o tema fora da vitrine
 * (visível, chato, corrigível) em vez de à venda para sempre (invisível, e o
 * item sazonal deixaria de ser sazonal sem ninguém notar).
 *
 * O typo em si falha ALTO em outro lugar: o `themeCatalogSync` cobra que todo
 * `season` referenciado por um tema exista nesta lista.
 *
 * `at` existe para o teste poder viajar no tempo sem mexer no relógio do
 * processo, e para o call site ser exercitável nas três bordas (véspera,
 * último dia, dia seguinte) sem fake timers.
 */
export function isSeasonOpen(seasonId: string | null | undefined, at: Date = new Date()): boolean {
  const season = getThemeSeason(seasonId);
  if (!season) return false;
  const today = dayKeyForBR(at);
  return today >= season.firstDay && today <= season.lastDay;
}

/**
 * Quantos dias ainda restam da temporada, contando hoje, ou `null` fora dela.
 *
 * Serve um aviso de "últimos dias" na vitrine. Derivado do calendário, nunca
 * digitado. Devolve 1 no último dia, que é o que "resta hoje" significa.
 */
export function seasonDaysRemaining(
  seasonId: string | null | undefined,
  at: Date = new Date(),
): number | null {
  const season = getThemeSeason(seasonId);
  if (!season || !isSeasonOpen(seasonId, at)) return null;
  const today = Date.parse(`${dayKeyForBR(at)}T00:00:00-03:00`);
  const last = Date.parse(`${season.lastDay}T00:00:00-03:00`);
  return Math.floor((last - today) / 86_400_000) + 1;
}

// ---------------------------------------------------------------------------
// A JANELA LIVRE — datas digitadas no painel, sem passar por temporada nomeada.
//
// A lista acima é código: mover a temporada exige deploy, e criar uma segunda
// exige editar o arquivo. O painel de temas (`admin-themes.md`) passou a gravar
// as duas pontas direto no doc do tema, e é isto que as lê.
//
// ## Por que CHAVE DE DIA, e não ms como o destaque
//
// `showcaseWindow` usa ms porque o destaque é alavanca operacional ("põe na
// frente até domingo às 20h"). A temporada é campanha de meses, e é comparada
// contra `dayKeyForBR` nas DUAS pontas do produto — vitrine e guard de débito.
// Gravar ms aqui obrigaria o servidor a converter fuso no caminho do dinheiro,
// que é exatamente o que a chave de dia existe para evitar.
//
// ## As duas pontas são INDEPENDENTES
//
// Só `firstDay` é "a partir de"; só `lastDay` é "até"; nenhuma é "vende
// sempre". É a mesma gramática do destaque, e ela cobre o caso real de "põe na
// loja agora e tira no fim do mês" sem exigir que o operador invente uma data
// de começo no passado.
//
// ⚠️ `lastDay` é INCLUSIVO, ao contrário do `until` do destaque. A temporada já
// nasceu assim (`ThemeSeason.lastDay`), o operador digita "30/11" pensando no
// dia 30 inteiro, e ter duas convenções para a mesma tela seria a diferença que
// só aparece quando alguém agenda duas temporadas em sequência.
// ---------------------------------------------------------------------------

/** Uma janela livre. Cada ponta é opcional e independente. */
export interface SeasonWindow {
  /** Primeiro dia, chave BR, **inclusivo**. Ausente = já vale. */
  firstDay?: string | null;
  /** Último dia, chave BR, **inclusivo**. Ausente = não termina. */
  lastDay?: string | null;
}

/**
 * A string é uma chave de dia utilizável?
 *
 * ⚠️ O `Number.isFinite` do `Date.parse` não é zelo: `"2026-13-45"` casa com a
 * forma e NÃO é uma data. Sem ele, uma janela impossível seria gravada e o tema
 * simplesmente nunca apareceria — a classe "salvei e nada mudou", que é o que
 * este módulo inteiro existe para evitar.
 */
export function isDayKey(v: unknown): v is string {
  if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const ms = Date.parse(v + "T00:00:00-03:00");
  if (!Number.isFinite(ms)) return false;
  // `Date.parse` normaliza "2026-02-31" para 03/03. Só é chave de dia o que
  // volta idêntico — senão o operador digita fevereiro e recebe março.
  return dayKeyForBR(new Date(ms)) === v;
}

/** A ponta utilizável de uma janela, ou `undefined`. */
function pontaUtil(v: unknown): string | undefined {
  return isDayKey(v) ? v : undefined;
}

/**
 * A janela livre gravada no painel, ou `null` quando o painel não opinou.
 *
 * Ponta malformada é DESCARTADA, nunca faz a janela inteira valer: um doc com
 * lixo numa das pontas ainda tem a outra dizendo algo verdadeiro, e recusar as
 * duas devolveria o tema à temporada do código sem ninguém pedir.
 */
export function adminSeasonWindow(config: {
  seasonFrom?: unknown;
  seasonUntil?: unknown;
} | null | undefined): SeasonWindow | null {
  const firstDay = pontaUtil(config?.seasonFrom);
  const lastDay = pontaUtil(config?.seasonUntil);
  if (!firstDay && !lastDay) return null;
  return { firstDay: firstDay ?? null, lastDay: lastDay ?? null };
}

/** A janela livre está aberta hoje? Sem janela = aberta. */
export function isSeasonWindowOpen(
  janela: SeasonWindow | null | undefined,
  at: Date = new Date(),
): boolean {
  if (!janela) return true;
  const today = dayKeyForBR(at);
  if (janela.firstDay && today < janela.firstDay) return false;
  if (janela.lastDay && today > janela.lastDay) return false;
  return true;
}

/**
 * A janela é coerente?
 *
 * Fim antes do começo é a única combinação sem significado: produz um tema que
 * nunca entra na loja, e o painel diria "salvo" sem nada mudar.
 *
 * ⚠️ Fim IGUAL ao começo é válido — é a temporada de um dia só, que é um pedido
 * legítimo (lançamento relâmpago). O `<` e não `<=` aqui é a diferença.
 */
export function isSeasonWindowSane(janela: SeasonWindow | null | undefined): boolean {
  if (!janela?.firstDay || !janela?.lastDay) return true;
  return janela.lastDay >= janela.firstDay;
}

/**
 * Quantos dias restam da janela livre, contando hoje, ou `null` se ela não
 * termina ou não está aberta. Serve o aviso de "últimos dias" na vitrine.
 */
export function seasonWindowDaysRemaining(
  janela: SeasonWindow | null | undefined,
  at: Date = new Date(),
): number | null {
  if (!janela?.lastDay || !isSeasonWindowOpen(janela, at)) return null;
  const today = Date.parse(dayKeyForBR(at) + "T00:00:00-03:00");
  const last = Date.parse(janela.lastDay + "T00:00:00-03:00");
  return Math.floor((last - today) / 86_400_000) + 1;
}
